import {
  AlertTriangle, Mail, Whatsapp
} from '@huspy/briks-icons';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { Image } from '@mantine/core';
import { getClientVaultUrl } from '@modules/core/utils';
import { iconsPath, imagesPath } from '@shared/css.const';
import { shareToWhatsapp } from '@shared/utils';
import { Trans, useTranslation } from 'react-i18next';
import { useClipboard } from '@mantine/hooks';
import { toast } from '@huspy/forge/shared';
import { useShareVaultViaEmail } from '@modules/opportunities/entities/opportunity/mutation/useShareVaultViaEmail';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import briksTheme from '@shared/briks-theme';
import { vaultConfigStyles } from '../styles';

const baseTranslation = 'vault.config.slido';

const Badge = ({ text }: { text: string }) => (
  <Box
    bg='alpha-white-70'
    borderColor='neutral.200'
    borderStyle='solid'
    borderWidth='1'
    borderRadius='4'
    px='2'
    py='1'
    w='fit-content'
  >
    <Text size='xs'>{text}</Text>
  </Box>
);

const DirectionArrow = ({
  text,
  direction,
}: {
  text: string;
  direction: 'left' | 'right';
}) => (
  <Flex position='relative' w='100%' justify='center'>
    <Image
      src={ `${iconsPath}/long-${direction}-arrow.svg` }
      pos='absolute'
      w='100%'
      top='30%'
      style={ { zIndex: '-1' } }
    />
    <Box bg='blue.200' borderRadius='2' px='3' py='2' w='fit-content'>
      <Text size='xs' fontWeight='medium'>
        {text}
      </Text>
    </Box>
  </Flex>
);

export const VaultDisclaimer = ({
  fullName,
  email,
  mobile,
  isValidData,
  hasApplicant,
  isBankSelected,
  missingPhoneOrEmail,
}: {
  fullName: string;
  email: string | undefined;
  mobile: string | undefined;
  isValidData: boolean;
  hasApplicant: boolean,
  isBankSelected: boolean,
  missingPhoneOrEmail: boolean;
}) => {
  const { t } = useTranslation();

  const getMessageKey = () => {
    if (!hasApplicant) return 'disclaimer.noApplicantFound';
    if (missingPhoneOrEmail) return 'disclaimer.emptyData';
    if (!isValidData) return 'disclaimer.inValidData';
    if (!isBankSelected) return 'disclaimer.banksNotSelected';

    return null;
  };

  const messageKey = getMessageKey();

  const notProvided = t(`${baseTranslation}.disclaimer.notProvided`);
  return (
    <Flex
      gap='2'
      align='start'
      bg='warning.50'
      p='2'
      borderRadius='2'
      direction='column'
    >
      {messageKey && (
        <Flex gap='2' align='center'>
          <Box flexShrink={ 0 }>
            <AlertTriangle
              height='30'
              width='30'
              color={ briksTheme.colors['warning.500'] }
            />
          </Box>
          <Text color='text-primary' size='md'>
            {t(`${baseTranslation}.${messageKey}`)}
          </Text>
        </Flex>
      )}
      {hasApplicant
      && (
        <Flex direction='column'>
          <Text fontWeight='bold' mb='2'>
            {t(`${baseTranslation}.disclaimer.title`)}
          </Text>
          <Text color='text-primary' size='md'>
            <Trans
              i18nKey={ t(`${baseTranslation}.disclaimer.fullName`, { fullName }) }
              components={ { strong: <strong /> } }
            />
          </Text>
          <Text color='text-primary' size='md'>
            <Trans
              i18nKey={ t(`${baseTranslation}.disclaimer.email`, { email: email ?? notProvided }) }
              components={ { strong: <strong /> } }
            />
          </Text>
          <Text color='text-primary' size='md'>
            <Trans
              i18nKey={ t(`${baseTranslation}.disclaimer.mobile`, { mobile: mobile ?? notProvided }) }
              components={ { strong: <strong /> } }
            />
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export const VaultActions = ({
  opportunityId,
  fullName,
  email,
  clientId,
  isDisabled = false,
}: {
  opportunityId: string;
  fullName: string;
  email: string | undefined;
  clientId: string | undefined;
  isDisabled?: boolean;
}) => {
  const { t } = useTranslation();

  const {
    mutateAsync: shareVaultViaEmail,
    isPending: isLoading,
  } = useShareVaultViaEmail(opportunityId, clientId);

  const shareMessage = `${t('vault.config.greeting', { name: fullName })}\n\n`
    + `${t('vault.config.clientVaultInstruction')}\n\n`
    + `${getClientVaultUrl(opportunityId)}\n\n`
    + `${t('vault.config.loginDetailsInstruction', { email })}`;
  const clipboard = useClipboard();

  return (
    <Flex direction='column' align='center' gap='2'>
      <Flex pt='2' gap='2' justify='flex-end'>
        <Button
          onClick={ () => shareVaultViaEmail() }
          data-testid='send-email-button'
          variant={ isLoading ? 'secondary' : 'primary' }
          loading={ isLoading }
          disabled={ isDisabled }
        >
          <Flex gap='2'>
            <Mail color='white' width='16' height='16' />
            <Text>{t(`${baseTranslation}.sendViaEmail`)}</Text>
          </Flex>
        </Button>
        <Button
          onClick={ () => {
            shareToWhatsapp(shareMessage);
            trackAmplitudeEvent(
              USER_EVENTS.VAULT.VAULT_SHARE_WHATSAPP_CLICKED,
              {
                case_id: opportunityId,
                client_id: clientId,
              }
            );
          } }
          data-testid='share-whatsapp-button'
          variant={ isDisabled ? 'primary' : 'outline' }
          disabled={ isDisabled }
        >
          <Flex gap='2'>
            <Whatsapp color={ isDisabled ? 'white' : 'black' } width='16' height='16' />
            <Text>Whatsapp</Text>
          </Flex>
        </Button>
        <Button
          onClick={ () => {
            clipboard.copy(getClientVaultUrl(opportunityId));
            toast('info', { message: t('vault.config.vaultUrlCopied') });
            trackAmplitudeEvent(
              USER_EVENTS.VAULT.VAULT_SHARE_COPY_LINK_CLICKED,
              {
                case_id: opportunityId,
                client_id: clientId,
              }
            );
          } }
          data-testid='copy-link-button'
          variant={ isDisabled ? 'primary' : 'outline' }
          disabled={ isDisabled }
        >
          <Text>{t(`${baseTranslation}.copyLink`)}</Text>
        </Button>
      </Flex>
      <Text color='text-secondary' size='md' fontStyle='italic'>
        {t(`${baseTranslation}.sendingTo`)}
        {' '}
        {email}
      </Text>
    </Flex>
  );
};

export const VaultOverview = ({
  opportunityId,
  fullName,
  email,
  clientId,
}: {
  opportunityId: string;
  fullName: string;
  email: string | undefined;
  clientId: string | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <Flex
      bg='linear-gradient(53.95deg, rgba(87, 136, 165, 0.2) 0%, rgba(159, 198, 209, 0.2) 88.07%)'
      p='6'
      direction='column'
      justify='center'
      align='center'
      gap='12'
    >
      <Flex direction='column' align='center'>
        <Text
          className={ vaultConfigStyles.gradientText }
          size='3xl'
          fontWeight='semibold'
          style={ { WebkitBackgroundClip: 'text' } }
          textAlign='center'
          w='70%'
        >
          {t(`${baseTranslation}.welcomeMessage`)}
        </Text>
        <Text size='md' color='blue.700' textAlign='center'>
          {t(`${baseTranslation}.assetMessage`)}
        </Text>
      </Flex>

      <Flex justify='space-between' h='78' w='100%'>
        <Flex direction='column' align='center'>
          <Image
            src={ `${imagesPath}/broker-blue.png` }
            width='108'
            height='108'
          />
          <Badge text='You' />
        </Flex>
        <Flex
          direction='column'
          gap='2'
          w='100%'
          justify='space-between'
          align='center'
        >
          <DirectionArrow
            direction='right'
            text={ t(`${baseTranslation}.sendVault`) }
          />
          <DirectionArrow
            direction='left'
            text={ t(`${baseTranslation}.clientFillVault`) }
          />
        </Flex>
        <Flex direction='column' align='center'>
          <Image
            src={ `${imagesPath}/client-purple.png` }
            width='108'
            height='108'
          />
          <Badge text='Client' />
        </Flex>
      </Flex>
      <VaultActions
        opportunityId={ opportunityId }
        fullName={ fullName }
        email={ email }
        clientId={ clientId }
      />
    </Flex>
  );
};
